package gr.tech.meetinggenie.components.layouts

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.meetinggenie.components.widgets.loadingSpinner
import gr.tech.meetinggenie.infrastructure.api.checkUserJWT
import gr.tech.meetinggenie.infrastructure.session.UserSession
import gr.tech.meetinggenie.models.user.USER_ROLES
import gr.tech.meetinggenie.navigation.SitePage
import gr.web.icyleds.components.layouts.PageContentStyle
import kotlinx.browser.document
import org.jetbrains.compose.web.css.fr
import org.jetbrains.compose.web.css.percent

@Composable
fun AuthPageLayout(
    title: String,
    redirectIfLoggedIn: String? = null,
    requiredRoles: List<String> = listOf(USER_ROLES.CONFIRMED_USER.role),
    content: @Composable ColumnScope.() -> Unit
) {
    val ctx = rememberPageContext()
    var hasValidAccess by remember { mutableStateOf(UserSession.checked.value) }

    LaunchedEffect(Unit) {
        document.title = title

        if (!hasValidAccess) {
            val userPreviewInfo = checkUserJWT(
                redirectLogin = requiredRoles.isNotEmpty(),
                redirectTo = SitePage.Login.route
            )

            if (userPreviewInfo.success && userPreviewInfo.data != null) {
                UserSession.info.value = userPreviewInfo.data
            } else {
                UserSession.info.value = null
            }

            UserSession.checked.value = true
        }

        val isLoggedIn = UserSession.isLoggedIn()

        // ----- REDIRECTION LOGIC -----

        // If the user is logged-in and you provided a redirect path (e.g. from login/signup),
        // send them there instead of letting them access this page.
        if (isLoggedIn && redirectIfLoggedIn != null) {
            ctx.router.tryRoutingTo(redirectIfLoggedIn)
            return@LaunchedEffect
        }

        // If user is not logged in and auth is required, bounce to login
        if (!isLoggedIn && requiredRoles.isNotEmpty()) {
            ctx.router.tryRoutingTo(SitePage.Login.route)
            return@LaunchedEffect
        }

        // ----- ROLE CHECK LOGIC -----

        // If roles are required, ensure the current user has at least one of them
        if (requiredRoles.isNotEmpty()) {
            val userRoles = UserSession.info.value?.roles ?: emptyList()

            val hasMatchingRole = userRoles.any { it in requiredRoles }

            if (!hasMatchingRole) {
                // User is logged in but doesn't have permission → bounce home
                ctx.router.tryRoutingTo(SitePage.Login.route)
                return@LaunchedEffect
            }
        }

        hasValidAccess = true
    }


    Box(
        Modifier
            .fillMaxWidth()
            .minHeight(100.percent)
            .gridTemplateRows { size(1.fr); size(minContent) },
        contentAlignment = Alignment.Center
    ) {
        Column(
            Modifier.fillMaxSize().gridRow(1),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Column(
                PageContentStyle.toModifier(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (hasValidAccess || (requiredRoles.isEmpty() && UserSession.checked.value)) {

                    // Render actual content if access is approved, otherwise show a loader
                    content()
                } else {
                    // Loading state (can swap with skeleton if needed)
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        loadingSpinner(iconSize = IconSize.X5)
                    }
                }
            }
        }
    }
}

